<template>
  <esg-base-chart>
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <apex-chart ref="chart"
                v-show="!loading"
                height="250"
                type="bar"
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>
<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  name: 'FirmwideEmissionByProductFamily',
  components: { EsgBaseChart },
  props: {
    sort: {
      type: String,
      default: 'dsc'
    },
    stackType: {
      type: String,
      default: 'normal'
    },
    year: {
      type: [String, Number],
      default: new Date().getFullYear()
    }
  },
  watch: {
    year: {
      immediate: true,
      handler() {
        this.fetchData();
      }
    },
    sort: {
      immediate: true,
      handler() {
        this.setSeries(this.list);
        this.setLabels();
        this.refreshChart();
      }
    },
  },
  computed: {
    lca() {
      return this.$store.getters['ghg/lifeCycleStages'];
    }
  },
  data() {
    return {
      loading: false,
      productFamilies: [],
      productFamilyCodes: [],
      list: [],
      series: [],
      chartOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          stackType: this.stackType,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: {
            columnWidth: '40%',
            dataLabels: {
              total: {
                enabled: true,
                formatter: (val) => this.formatNumber(val, 0),
                style: {
                  fontWeight: 600,
                }
              }
            }
          },
        },
        dataLabels: {
          enabled: this.stackType === '100%',
        },
        xaxis: {
          categories: [],
          crosshairs: { show: false }
        },
        yaxis: {
          show: this.stackType === 'normal',
          title: {
            text: '탄소 배출량 (tCO2)',
            offsetX: 3,
            style: {
              fontSize: '12px',
              fontWeight: 'normal',
            },
          },
          labels: {
            formatter: val => this.compactFormatNumber(val)
          }
        },
        legend: {
          position: 'bottom',
        },
        fill: {
          opacity: 1
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, data) => {
              let formattedValue = this.formatNumber(val) + " tCO2";
              let dataPointIndex = data.dataPointIndex;
              let seriesIndex = data.seriesIndex;
              let productFamilyDataPoints = data.series.map(s => s[dataPointIndex]);
              let indexOfMax = this.indexOfMax(productFamilyDataPoints);

              if (seriesIndex === indexOfMax && val !== 0) {
                return `<span style="color: #F54540">${formattedValue}</span>`
              } else {
                return formattedValue;
              }
            }
          }
        },
        grid: {
          show: false,
        },
      },
    }
  },
  created() {
    this.fetchData()
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        // Fetch product families
        this.abortController = new AbortController();
        let response = await axios.get('esg/lca/tables/esg_base_product_family', {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.list) return;
        this.productFamilies = response.data.list;

        // Construct query parameter
        let args = [{
          years: [this.year],
          groupKeys: ['year', 'product_family_code', 'product_family_name', 'category']
        }];

        let param = new FormData();
        param.append('path', 'esg/lca/ghg_emission/main')
        param.append('json', JSON.stringify(args))

        // Fetch chart data
        response = await axios.post('esg/lca/run/python/chart-data', param, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }
        this.list = JSON.parse(response.data.data)[0].data;

        this.setTotals(this.list);
        this.setSeries(this.list);
        this.setLabels();
      } catch (error) {
        this.loading = false
        await this.showError(error)
      } finally {
        this.loading = false
        this.refreshChart()
      }
    },
    setTotals(list) {
      // Calculate the total GHG emission for product families
      this.productFamilies.forEach(pf => {
        pf.total = list
            .filter(row => row.product_family_code === pf.code)
            .reduce((sum, el) => sum + el.values, 0);
      });
      // Sort product families based on the totals (ascending order)
      this.productFamilies.sort((a, b) => a.total - b.total);
    },
    setSeries(list) {
      // Extract product family codes
      let productFamilyCodes = this.productFamilies.map(pf => pf.code);
      if (this.sort === 'dsc') {
        productFamilyCodes.reverse();
      }

      // Slice the top 10 product families
      productFamilyCodes = productFamilyCodes.slice(0, 10);

      // Extract data
      this.series = this.lca.map(lca => ({
            name: lca.label,
            data: productFamilyCodes.map(code => {
              let row = list.find(row => row.product_family_code === code && row.category === lca.value);
              return row ? row.values : 0;
            })
          })
      );
    },
    setLabels() {
      let labels = this.productFamilies.map(pf => pf.name);
      if (this.sort === 'dsc') labels.reverse();
      this.chartOptions.xaxis.categories = labels.slice(0, 10);
    },
    indexOfMax(arr) {
      if (arr.length === 0) {
        return -1;
      }

      let max = arr[0];
      let maxIndex = 0;

      for (let i = 1; i < arr.length; i++) {
        if (arr[i] > max) {
          maxIndex = i;
          max = arr[i];
        }
      }

      return maxIndex;
    }
  }
}
</script>